import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../lib/adminApi";
import { adminErrorRedirect } from "../lib/loadResult";

interface FlatItem {
  id: string;
  label: string;
  sub?: string;
  to: string;
}

interface Group {
  header: string;
  items: FlatItem[];
}

// The 10 real routes (the phase-4 /run route is intentionally excluded).
const PAGES: Array<{ label: string; to: string }> = [
  { label: "Overview", to: "/" },
  { label: "Tokens", to: "/tokens" },
  { label: "Companies", to: "/companies" },
  { label: "Users", to: "/users" },
  { label: "Accountants", to: "/accountants" },
  { label: "Subscriptions", to: "/subscriptions" },
  { label: "Server", to: "/server" },
  { label: "Tax readiness", to: "/tax" },
  { label: "Deploys", to: "/deploys" },
  { label: "Audit", to: "/audit" },
];

interface CompanyHit {
  company_id: string;
  name: string;
  owner_email: string;
}

interface UserHit {
  user_id: string;
  email: string;
  full_name: string | null;
}

export default function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [companies, setCompanies] = useState<CompanyHit[]>([]);
  const [users, setUsers] = useState<UserHit[]>([]);
  const [remoteError, setRemoteError] = useState<string | null>(null);
  const reqCounter = useRef(0);

  // Global toggle (Ctrl/Cmd+K) and Escape-to-close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => {
          const next = !prev;
          if (next) {
            // Reset query/selection whenever it opens.
            setQuery("");
            setSelectedIndex(0);
            setCompanies([]);
            setUsers([]);
            setRemoteError(null);
          }
          return next;
        });
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Reset selection whenever the query changes.
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Debounced remote search — only when the palette is open and the trimmed
  // query is at least 2 chars. Stale responses are ignored via a counter.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) {
      setCompanies([]);
      setUsers([]);
      setRemoteError(null);
      return;
    }
    const reqId = ++reqCounter.current;
    const t = setTimeout(() => {
      void (async () => {
        try {
          const [companyRes, userRes] = await Promise.all([
            adminApi.companiesList({ search: q, limit: 5 }),
            adminApi.usersList({ q, limit: 5 }),
          ]);
          if (reqId !== reqCounter.current) return;
          setCompanies(
            companyRes.companies.map((c) => ({
              company_id: c.company_id,
              name: c.name,
              owner_email: c.owner_email,
            })),
          );
          setUsers(
            userRes.users.map((u) => ({
              user_id: u.user_id,
              email: u.email,
              full_name: u.full_name,
            })),
          );
          setRemoteError(null);
        } catch (err) {
          if (reqId !== reqCounter.current) return;
          const to = adminErrorRedirect(err);
          if (to) {
            setOpen(false);
            navigate(to, { replace: true });
            return;
          }
          setCompanies([]);
          setUsers([]);
          setRemoteError(err instanceof Error ? err.message : "Search failed");
        }
      })();
    }, 250);
    return () => clearTimeout(t);
  }, [query, open, navigate]);

  const groups = useMemo<Group[]>(() => {
    const needle = query.trim().toLowerCase();
    const pageItems: FlatItem[] = PAGES.filter((p) =>
      p.label.toLowerCase().includes(needle),
    ).map((p) => ({ id: `page:${p.to}`, label: p.label, to: p.to }));
    const companyItems: FlatItem[] = companies.map((c) => ({
      id: `company:${c.company_id}`,
      label: c.name,
      sub: c.owner_email,
      to: `/companies/${c.company_id}`,
    }));
    const userItems: FlatItem[] = users.map((u) => ({
      id: `user:${u.user_id}`,
      label: u.email,
      sub: u.full_name ?? undefined,
      to: `/users/${u.user_id}`,
    }));
    return [
      { header: "Pages", items: pageItems },
      { header: "Companies", items: companyItems },
      { header: "Users", items: userItems },
    ];
  }, [query, companies, users]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const select = (item: FlatItem) => {
    setOpen(false);
    navigate(item.to);
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => (flat.length ? (i + 1) % flat.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) =>
        flat.length ? (i - 1 + flat.length) % flat.length : 0,
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = flat[selectedIndex];
      if (item) select(item);
    }
  };

  if (!open) return null;

  let flatIndex = -1;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center bg-black/50"
      onClick={() => setOpen(false)}
    >
      <div
        className="mt-[12vh] flex max-h-[70vh] w-full max-w-xl flex-col overflow-hidden rounded-lg border border-border bg-panel shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          autoFocus
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKeyDown}
          placeholder="Search pages, companies, users…"
          className="w-full border-b border-border bg-panel px-4 py-3 text-sm placeholder:text-muted focus:outline-none"
        />

        {remoteError ? (
          <div className="px-4 py-2 text-xs text-danger">{remoteError}</div>
        ) : null}

        <div className="overflow-y-auto py-1">
          {flat.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-muted">
              No matches.
            </div>
          ) : (
            groups.map((g) =>
              g.items.length === 0 ? null : (
                <div key={g.header} className="py-1">
                  <div className="px-4 py-1 text-[10px] uppercase tracking-wide text-muted">
                    {g.header}
                  </div>
                  {g.items.map((item) => {
                    flatIndex += 1;
                    const idx = flatIndex;
                    const active = idx === selectedIndex;
                    return (
                      <button
                        key={item.id}
                        onClick={() => select(item)}
                        onMouseEnter={() => setSelectedIndex(idx)}
                        className={[
                          "flex w-full items-baseline gap-2 px-4 py-2 text-left text-sm",
                          active ? "bg-bg text-fg" : "text-muted",
                        ].join(" ")}
                      >
                        <span className="truncate text-fg">{item.label}</span>
                        {item.sub ? (
                          <span className="truncate text-xs text-muted">
                            {item.sub}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ),
            )
          )}
        </div>
      </div>
    </div>
  );
}
